import type { SurvivalClassYear } from '../../../services/gameScores';

// Identity for account-less /play/[school] guests, persisted in the browser
// so repeat plays rank as a single leaderboard entry (best score).
const GUEST_ID_KEY = 'oraPlayGuestId';
const GUEST_NAME_KEY = 'oraPlayGuestName';
const GUEST_CLASS_YEAR_KEY = 'oraPlayClassYear';

export const GUEST_NAME_MAX_LENGTH = 32;

export interface GuestIdentity {
  guestId: string;
  guestName: string;
  classYear: SurvivalClassYear | null;
}

const generateGuestId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `guest-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

// Mirrors the backend's sanitizeGuestName so what the guest sees locally is
// what the board will show.
export const sanitizeGuestName = (raw: string): string =>
  raw
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, GUEST_NAME_MAX_LENGTH)
    .trim();

export const getGuestIdentity = (): GuestIdentity | null => {
  const guestId = localStorage.getItem(GUEST_ID_KEY);
  const guestName = localStorage.getItem(GUEST_NAME_KEY);
  const storedClassYear = localStorage.getItem(GUEST_CLASS_YEAR_KEY);

  if (!guestId || !guestName) return null;

  const classYear: SurvivalClassYear | null =
    storedClassYear === 'M1' ||
    storedClassYear === 'M2' ||
    storedClassYear === 'M3' ||
    storedClassYear === 'M4' ||
    storedClassYear === 'OTHER'
      ? storedClassYear
      : null;

  return { guestId, guestName, classYear };
};

// Called when a user logs in (all flows): a signed-in person should play
// under their account, not a leftover guest persona — without this, a
// guest who later logs in keeps landing on the board under the old guest
// name. Their previous guest rows stay on the board (separate table) but
// lose the "(You)" tag.
export const clearGuestIdentity = (): void => {
  localStorage.removeItem(GUEST_ID_KEY);
  localStorage.removeItem(GUEST_NAME_KEY);
  localStorage.removeItem(GUEST_CLASS_YEAR_KEY);
};

// Stores the sanitized name and required class year, creating a stable
// guestId on first use. A returning attendee keeps the same guestId.
export const saveGuestIdentity = (
  rawName: string,
  classYear: SurvivalClassYear,
  // Used ONLY when this browser has no guest id yet, so a caller that can derive
  // a stable id (e.g. from a signed-in user id) makes that player's devices land
  // on one leaderboard row instead of one per device. An already-stored id is
  // never replaced: it may already own scores on a board.
  preferredGuestId?: string
): GuestIdentity | null => {
  const guestName = sanitizeGuestName(rawName);

  if (!guestName) return null;

  const guestId =
    localStorage.getItem(GUEST_ID_KEY) ?? preferredGuestId ?? generateGuestId();

  localStorage.setItem(GUEST_ID_KEY, guestId);
  localStorage.setItem(GUEST_NAME_KEY, guestName);
  localStorage.setItem(GUEST_CLASS_YEAR_KEY, classYear);

  return { guestId, guestName, classYear };
};
